import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { bluetoothService, BtDeviceInfo, ScaleReading, DeviceType } from '../services/bluetooth.service';
import { LabelSize } from './label-template.store';

export type PrintSize = LabelSize;

interface BluetoothState {
    // Scale
    scaleDevice: BtDeviceInfo | null;
    scaleConnecting: boolean;
    lastWeight: ScaleReading | null;

    // Printer
    printerDevice: BtDeviceInfo | null;
    printerConnecting: boolean;
    printSize: PrintSize;
    printProtocol: 'escpos' | 'tspl' | 'cpcl' | 'auto';
    printDensity: number;
    printGap: number;
    printAutoDetectGap: boolean;

    // Scanning UI
    isScanning: boolean;
    scanTarget: DeviceType | null;

    // Actions
    connectScale: () => Promise<void>;
    connectScaleAll: () => Promise<void>;
    disconnectScale: () => void;
    connectPrinter: () => Promise<void>;
    connectPrinterAll: () => Promise<void>;
    disconnectPrinter: () => void;
    setPrintSize: (size: PrintSize) => void;
    setPrintProtocol: (protocol: 'escpos' | 'tspl' | 'cpcl' | 'auto') => void;
    setPrintDensity: (density: number) => void;
    setPrintGap: (gap: number) => void;
    setPrintAutoDetectGap: (auto: boolean) => void;
    setScanning: (scanning: boolean, target?: DeviceType | null) => void;
    updateWeight: (reading: ScaleReading) => void;
    handleDisconnect: (type: DeviceType) => void;
    autoReconnect: () => Promise<void>;
    resetAll: () => void;
    setBulk: (settings: Partial<BluetoothState>) => void;
}

export const useBluetoothStore = create<BluetoothState>()(
    persist(
        (set, get) => {
            // Register callbacks
            bluetoothService.setOnWeightChange((reading) => {
                set({ lastWeight: reading });
            });
            bluetoothService.setOnDisconnect((type) => {
                get().handleDisconnect(type);
            });

            return {
                scaleDevice: null,
                scaleConnecting: false,
                lastWeight: null,
                printerDevice: null,
                printerConnecting: false,
                printSize: '76x130',
                printProtocol: 'auto',
                printDensity: 8,
                printGap: 3,
                printAutoDetectGap: false,
                isScanning: false,
                scanTarget: null,

                connectScale: async () => {
                    set({ scaleConnecting: true, isScanning: true, scanTarget: 'scale' });
                    try {
                        const device = await bluetoothService.connectScale();
                        set({ scaleDevice: device, scaleConnecting: false, isScanning: false, scanTarget: null });
                    } catch (err: any) {
                        set({ scaleConnecting: false, isScanning: false, scanTarget: null });
                        throw err;
                    }
                },

                connectScaleAll: async () => {
                    set({ scaleConnecting: true, isScanning: true, scanTarget: 'scale' });
                    try {
                        const device = await bluetoothService.connectScaleAll();
                        set({ scaleDevice: device, scaleConnecting: false, isScanning: false, scanTarget: null });
                    } catch (err: any) {
                        set({ scaleConnecting: false, isScanning: false, scanTarget: null });
                        throw err;
                    }
                },

                disconnectScale: () => {
                    bluetoothService.disconnectScale();
                    set({ scaleDevice: null, lastWeight: null });
                },

                connectPrinter: async () => {
                    set({ printerConnecting: true, isScanning: true, scanTarget: 'printer' });
                    try {
                        const device = await bluetoothService.connectPrinter();
                        set({ printerDevice: device, printerConnecting: false, isScanning: false, scanTarget: null });
                    } catch (err: any) {
                        set({ printerConnecting: false, isScanning: false, scanTarget: null });
                        throw err;
                    }
                },

                connectPrinterAll: async () => {
                    set({ printerConnecting: true, isScanning: true, scanTarget: 'printer' });
                    try {
                        const device = await bluetoothService.connectPrinterAll();
                        set({ printerDevice: device, printerConnecting: false, isScanning: false, scanTarget: null });
                    } catch (err: any) {
                        set({ printerConnecting: false, isScanning: false, scanTarget: null });
                        throw err;
                    }
                },

                disconnectPrinter: () => {
                    bluetoothService.disconnectPrinter();
                    set({ printerDevice: null });
                },

                setPrintSize: (size) => set({ printSize: size }),

                setPrintProtocol: (protocol) => set({ printProtocol: protocol }),

                setPrintDensity: (density) => set({ printDensity: density }),

                setPrintGap: (gap) => set({ printGap: gap }),

                setPrintAutoDetectGap: (auto) => set({ printAutoDetectGap: auto }),

                setScanning: (scanning, target = null) => set({ isScanning: scanning, scanTarget: target }),

                updateWeight: (reading) => set({ lastWeight: reading }),

                handleDisconnect: (type) => {
                    if (type === 'scale') {
                        set({ scaleDevice: null, lastWeight: null });
                    } else {
                        set({ printerDevice: null });
                    }
                },

                autoReconnect: async () => {
                    if (!bluetoothService.isSupported()) return;

                    // Guard against double invocation (React strict mode calls useEffect twice)
                    if ((window as any).__btAutoReconnecting) {
                        console.log('[BT Store] Auto-reconnect already in progress, skipping');
                        return;
                    }
                    (window as any).__btAutoReconnecting = true;

                    // Read persisted device names from localStorage
                    const persisted = JSON.parse(localStorage.getItem('hedui-bluetooth') || '{}')?.state || {};
                    const lastPrinterName = persisted._lastPrinterName;
                    const lastScaleName = persisted._lastScaleName;

                    if (lastScaleName && !get().scaleDevice) {
                        try {
                            console.log(`[BT Store] Auto-reconnecting scale: ${lastScaleName}`);
                            const scale = await bluetoothService.tryAutoConnect('scale', lastScaleName);
                            if (scale) {
                                console.log('[BT Store] ✅ Scale auto-reconnect SUCCESS');
                                set({ scaleDevice: scale });
                            }
                        } catch (err) {
                            console.log('[BT Store] Scale auto-reconnect failed');
                        }
                    }

                    if (lastPrinterName && !get().printerDevice) {
                        try {
                            console.log(`[BT Store] Auto-reconnecting printer: ${lastPrinterName}`);
                            const printer = await bluetoothService.tryAutoConnect('printer', lastPrinterName);
                            if (printer) {
                                console.log('[BT Store] ✅ Printer auto-reconnect SUCCESS:', printer.name);
                                set({ printerDevice: printer });
                            } else {
                                console.log('[BT Store] Printer not found or not available');
                            }
                        } catch (err: any) {
                            console.log('[BT Store] Printer auto-reconnect failed:', err.message);
                        }
                    }

                    (window as any).__btAutoReconnecting = false;
                },

                resetAll: () => {
                    bluetoothService.disconnectAll();
                    set({
                        scaleDevice: null,
                        printerDevice: null,
                        lastWeight: null,
                        isScanning: false,
                        scanTarget: null
                    });
                },
                setBulk: (settings) => set((state) => ({ ...state, ...settings })),
            };
        },
        {
            name: 'hedui-bluetooth',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                printSize: state.printSize,
                printProtocol: state.printProtocol,
                printDensity: state.printDensity,
                printGap: state.printGap,
                printAutoDetectGap: state.printAutoDetectGap,
                // Persist device names for auto-reconnect after page refresh
                _lastPrinterName: state.printerDevice?.name || null,
                _lastScaleName: state.scaleDevice?.name || null,
            }),
        }
    )
);
